import {
  IsVaultDeletableApi,
  type Condition,
  ListVaultBlockedRegionsApi,
  type Configuration,
  ResourceType,
  VaultsApi,
  type CreateVaultRequest,
  type VaultsFilterConditions,
} from '@repo/api-gw-sdk';

import type { BackupVault } from '@/data/vaults/backupVault';
import { convertQuery } from '@/utils/conditionQuery';
import { encodePageToken } from '@/utils/string';

import type { HttpClient } from './httpClient';

const baseUrl = '/vaults';

// DO NOT Lower this, unless confirming it does not regress customer issues. see EON-9177
const UNPAGED_SIZE = 2000;

/** Matches every cached page of the vaults list, whatever its page or filters. */
export const isVaultsListKey = (key: unknown) =>
  Array.isArray(key) && key[0] === baseUrl;

export const vaults = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const vaultsApi = new VaultsApi(config);
  const isVaultDeletableApi = new IsVaultDeletableApi(config);
  const listVaultBlockedRegionsApi = new ListVaultBlockedRegionsApi(config);
  return {
    // Called with no arguments this is the whole vault list, which most of the console reads as one
    // cached response; passing a condition or a page gives the vaults page its own filtered slice.
    useList: (options?: {
      condition?: Condition;
      pageIndex?: number;
      pageSize?: number;
      enabled?: boolean;
    }) => {
      const filters = convertQuery<VaultsFilterConditions>(options?.condition);
      return httpClient.execute(
        vaultsApi.listVaults.bind(vaultsApi),
        options?.enabled === false ? null : baseUrl,
        '/vaults',
        currentProjectId,
        options?.pageIndex ? encodePageToken(options.pageIndex) : undefined,
        options?.pageSize ?? UNPAGED_SIZE,
        filters ? { filters } : undefined
      );
    },
    create: (vault: BackupVault) => {
      // Convert BackupVault to CreateBackupVaultInput
      const createInput: CreateVaultRequest = {
        name: vault.name,
        vaultAccountId: vault.vaultAccountId,
        region: vault.region,
        vaultAttributes: {
          cloudProvider: vault.vaultAttributes.cloudProvider,
          aws: {
            encryptionKey: vault.vaultAttributes.aws?.encryptionKey,
            mrapAlias: vault.vaultAttributes.aws?.mrapAlias,
          },
          azure: {
            tenantId: vault.vaultAttributes.azure?.tenantId,
            encryptionKey: vault.vaultAttributes.azure?.encryptionKey,
            cmkKeyVaultTenantId:
              vault.vaultAttributes.azure?.cmkKeyVaultTenantId,
            cmkAppRegistrationId:
              vault.vaultAttributes.azure?.cmkAppRegistrationId,
          },
          gcp: {
            encryptionKey: vault.vaultAttributes.gcp?.encryptionKey,
          },
        },
        enableCspAssistedImmutability: vault.enableCspAssistedImmutability,
        labels: vault.labels,
      };

      return vaultsApi.createVault(currentProjectId, createInput);
    },
    useListAzureCmkEligibleApps: (
      vaultAccountId: string,
      customerTenantId: string | undefined
    ) =>
      httpClient.execute(
        vaultsApi.listAzureCmkEligibleApps.bind(vaultsApi),
        !vaultAccountId || !customerTenantId ? null : baseUrl,
        '/azure-cmk-eligible-apps',
        currentProjectId,
        vaultAccountId,
        customerTenantId ?? ''
      ),
    update: (vault: BackupVault) =>
      vaultsApi.updateVault(currentProjectId, vault.id, {
        name: vault.name,
        labels: vault.labels,
      }),
    reconcileBucketPolicy: (vaultId: string) =>
      vaultsApi.reconcileVaultBucketPolicy(currentProjectId, vaultId),
    delete: (vaultId: string) =>
      vaultsApi.deleteVault(currentProjectId, vaultId),
    useBlockedRegions: (resourceType: ResourceType | undefined) =>
      httpClient.execute(
        listVaultBlockedRegionsApi.listVaultBlockedRegions.bind(
          listVaultBlockedRegionsApi
        ),
        resourceType ? baseUrl : null,
        '/blocked-regions',
        currentProjectId,
        resourceType ?? ResourceType.ResourceTypeUnspecified
      ),
    isVaultDeletable: (vaultId: string) =>
      isVaultDeletableApi.isVaultDeletable(currentProjectId, vaultId),
  };
};
